import React, { useEffect, useState } from 'react';
import { Modal, Pressable, Switch, Text, TextInput, View } from 'react-native';
import {
  OperationalFilterFieldLabel,
  OperationalFilterSegmentedRow,
} from '../../../shared/filters/OperationalFilterPrimitives';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { SettingsSaveCta, hubStyles } from './settingsHubUi';
import { settingsStyles as styles } from '../settingsScreen.styles';
import {
  updatePickupDisplay,
  type PickupDisplayProfile,
  type PickupDisplayRow,
} from '../../infra/pickupDisplayRepo';

type NameVisibility = 'full' | 'first_name' | 'hidden';

type Props = {
  visible: boolean;
  row: PickupDisplayRow | null;
  onClose: () => void;
  onSaved: () => void;
};

export function PickupDisplaySettingsModal({ visible, row, onClose, onSaved }: Props) {
  const [showPreparing, setShowPreparing] = useState(true);
  const [playSound, setPlaySound] = useState(true);
  const [dwell, setDwell] = useState('10');
  const [nameVisibility, setNameVisibility] = useState<NameVisibility>('full');
  const [profile, setProfile] = useState<PickupDisplayProfile>('pickup');
  const [includeDineIn, setIncludeDineIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible || !row) return;
    const s = row.settings ?? {};
    setShowPreparing(s.show_preparing !== false);
    setPlaySound(s.play_sound_on_ready !== false);
    setDwell(String(s.ready_dwell_minutes ?? 10));
    setNameVisibility((s.name_visibility as NameVisibility) ?? 'full');
    setProfile(row.profile);
    setIncludeDineIn(s.include_dine_in === true);
    setError(null);
  }, [visible, row]);

  const save = async () => {
    if (!row) return;
    const dwellNum = Math.max(0, Math.min(120, parseInt(dwell.replace(/\D/g, ''), 10) || 10));
    setBusy(true);
    setError(null);
    try {
      await updatePickupDisplay(row.id, {
        profile,
        settings: {
          show_preparing: showPreparing,
          play_sound_on_ready: playSound,
          ready_dwell_minutes: dwellNum,
          name_visibility: nameVisibility,
          include_dine_in: includeDineIn,
        },
      });
      onSaved();
      onClose();
    } catch {
      setError('No pudimos guardar. Probá de nuevo.');
    } finally {
      setBusy(false);
    }
  };

  if (!row) return null;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'center', padding: 24 }}
        onPress={onClose}
      >
        <Pressable
          style={{
            backgroundColor: ticketColors.paper,
            borderRadius: 12,
            padding: 20,
            maxWidth: 480,
            width: '100%',
            alignSelf: 'center',
          }}
          onPress={(e) => e.stopPropagation()}
        >
          <Text style={styles.cardTitle}>Ajustes de {row.label}</Text>
          <Text style={hubStyles.hintSecondary}>
            Cambios en la TV al refrescar (casi al instante si hay Realtime).
          </Text>

          <View style={{ marginTop: 16, gap: 12 }}>
            <OperationalFilterFieldLabel>Perfil</OperationalFilterFieldLabel>
            <OperationalFilterSegmentedRow
              options={[
                { value: 'pickup', label: 'Retiro' },
                { value: 'delivery', label: 'Delivery' },
                { value: 'both', label: 'Ambos' },
              ]}
              value={profile}
              onChange={(v) => setProfile(v as PickupDisplayProfile)}
            />

            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <View style={{ flex: 1, paddingRight: 12 }}>
                <Text style={styles.captionBlock}>Comer acá en pantalla</Text>
                <Text style={hubStyles.hintSecondary}>
                  Pedís, te buscás la mesa: el # sale igual que retiro. Solo Retiro o Ambos.
                </Text>
              </View>
              <Switch
                value={includeDineIn}
                onValueChange={setIncludeDineIn}
                disabled={profile === 'delivery'}
              />
            </View>

            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text style={styles.captionBlock}>Mostrar preparando</Text>
              <Switch value={showPreparing} onValueChange={setShowPreparing} />
            </View>

            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text style={styles.captionBlock}>Sonido al listo</Text>
              <Switch value={playSound} onValueChange={setPlaySound} />
            </View>

            <OperationalFilterFieldLabel>Minutos en pantalla (listos)</OperationalFilterFieldLabel>
            <TextInput
              value={dwell}
              onChangeText={setDwell}
              keyboardType="number-pad"
              style={{
                borderWidth: 1,
                borderColor: ticketColors.rule,
                borderRadius: 8,
                padding: 10,
                fontSize: 16,
              }}
            />

            <OperationalFilterFieldLabel>Nombre del cliente</OperationalFilterFieldLabel>
            <OperationalFilterSegmentedRow
              options={[
                { value: 'full', label: 'Completo' },
                { value: 'first_name', label: 'Solo nombre' },
                { value: 'hidden', label: 'Oculto' },
              ]}
              value={nameVisibility}
              onChange={(v) => setNameVisibility(v as NameVisibility)}
            />
          </View>

          {error ? <Text style={{ marginTop: 8, color: ticketColors.bad }}>{error}</Text> : null}

          <View style={{ marginTop: 20, flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <SettingsSaveCta label="Cancelar" variant="secondary" layoutWide={false} onPress={onClose} />
            <SettingsSaveCta
              label={busy ? 'Guardando…' : 'Guardar'}
              layoutWide={false}
              disabled={busy}
              onPress={() => void save()}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}
